import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Lock, Eye, EyeOff, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLogin() {
  const [password, setPassword]   = useState("");
  const [show, setShow]           = useState(false);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState("");
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem("adminToken", data.token);
      navigate("/admin/dashboard");
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F3F4F6] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-2">
            <svg width="54" height="46" viewBox="0 0 54 46" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="jgl" gradientUnits="userSpaceOnUse" x1="16" y1="0" x2="36" y2="0">
                  <stop offset="50%" stopColor="#0F62FE"/>
                  <stop offset="50%" stopColor="#EF4444"/>
                </linearGradient>
              </defs>
              <path d="M3,23 A23,15 0 0,1 51,23" stroke="#EF4444" strokeWidth="3.5" fill="none" strokeLinecap="round"/>
              <path d="M51,23 A23,15 0 0,1 3,23" stroke="#0F62FE" strokeWidth="3.5" fill="none" strokeLinecap="round"/>
              <text x="16" y="34" fontSize="26" fontWeight="900" fontFamily="Arial Black,Arial,sans-serif" fill="url(#jgl)">J</text>
            </svg>
            <div className="text-left leading-none">
              <p className="font-franklin text-[#0F62FE] text-xs tracking-widest">JAIN</p>
              <p className="font-franklin text-[#0F62FE] text-[10px] tracking-[0.45em]">INFOTECH</p>
            </div>
          </div>
          <p className="text-[#4B5563] text-sm mt-2">Admin Panel — Enquiry Dashboard</p>
        </div>

        {/* Card */}
        <div className="bg-white border border-gray-200 shadow-lg p-8" data-testid="admin-login-card">
          <div className="flex items-center gap-2 mb-6">
            <div className="w-9 h-9 bg-[#EFF6FF] flex items-center justify-center">
              <Lock size={17} className="text-[#0F62FE]" />
            </div>
            <div>
              <h1 className="font-heading font-black text-[#111827] text-base leading-none">Admin Login</h1>
              <p className="text-xs text-[#4B5563] mt-0.5">Enter your password to continue</p>
            </div>
          </div>

          <form onSubmit={handleLogin} className="space-y-4" data-testid="admin-login-form">
            <div>
              <label className="block text-xs font-semibold text-[#374151] uppercase tracking-wide mb-1.5">
                Password
              </label>
              <div className="relative">
                <input
                  type={show ? "text" : "password"}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="Enter admin password"
                  required
                  data-testid="admin-password-input"
                  className="w-full px-4 py-3 border border-gray-300 text-sm pr-10 focus:outline-none focus:ring-2 focus:ring-[#0F62FE] focus:border-transparent"
                />
                <button
                  type="button"
                  onClick={() => setShow(!show)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {show ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="admin-login-error">
                <AlertCircle size={15} /> {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-btn"
              className="w-full flex items-center justify-center gap-2 bg-[#0F62FE] hover:bg-[#0052CC] disabled:bg-gray-400 text-white font-semibold py-3.5 transition-all duration-200 hover:shadow-lg text-sm"
            >
              {loading ? (
                <span className="animate-spin border-2 border-white border-t-transparent rounded-full w-4 h-4" />
              ) : "Login to Dashboard"}
            </button>
          </form>
        </div>

        <p className="text-center text-xs text-gray-400 mt-4">
          Jain Infotech &mdash; Internal Admin Panel
        </p>
      </div>
    </div>
  );
}
